// ============================================================================
// The project page's For finance panel, moved out of project-detail.tsx on
// 2026-10-02 (item #680) so what it SAYS can be rendered and read in a test.
// The money reads top to bottom as a story, and each fact appears ONCE on the
// page — which is why the separate Sub payment box was removed when the
// "Lines paid" row arrived (TCSD-0380).
// ============================================================================
import type React from 'react';
import { AlertTriangle } from 'lucide-react';
import { fmtCurrency } from './lib/fmt-currency';
import { fmtDate } from './lib/fmt-date';

// margin_pct is stored as percentage form (29.95 = 29.95%) by trigger
// trg_projects_compute_margin. Do not multiply by 100.
export function fmtPct(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${Number(n).toFixed(1)}%`;
}

// Stoplight color class for margin pct text. Driven by margin_status which the
// DB trigger sets to Green / Amber / Red against the 26.5% target.
export function marginColorClass(status: string | null | undefined): string {
  switch (status) {
    case 'Green':         return 'text-rag-green';
    case 'Amber':         return 'text-st-warn-fg';
    case 'Red':           return 'text-rag-red';
    case 'Internal Cost': return 'text-tcs-text-muted';
    default:              return 'text-tcs-text-muted';
  }
}

/**
 * The Finance panel — Option B, 2026-08-21.
 *
 * The left column of the card is the work, which an AM edits. This is the other
 * side: the numbers Finance reads, each appearing exactly ONCE on the page and
 * none of them repeated on the left. That duplication is what made this card 28
 * fields long, with stage rendered three times and bill and pay three times each.
 *
 * The money reads top to bottom as a story: what the client pays, what we pay
 * out, what we get back, what the event actually costs us.
 */
export function FinancePanel({
  facilityName, facilityId, accountName,
  bill, paidOut, chargedBack, netCost,
  marginAmount, marginPct: pct, marginStatus,
  workDone, classification, stageLabel, linesPaid = null,
}: {
  facilityName: string | null; facilityId: string | null; accountName: string | null;
  bill: number | null; paidOut: number | null; chargedBack: number; netCost: number | null;
  marginAmount: number | null; marginPct: number | null; marginStatus: string | null;
  workDone: string | null; classification: string | null; stageLabel: string | null;
  /** Item #680: how far paying each line's own sub has got (lib/line-owned-payment.ts). */
  linesPaid?: string | null;
}) {
  // A Recovery or a Concession is not billed on purpose, so a margin of −100% is
  // noise rather than news. Show it only where being billed was the point.
  const showMargin =
    classification === null && bill != null && bill > 0 && marginAmount != null && pct != null;
  return (
    <div className="rounded-md border border-tcs-border bg-tcs-bg">
      <div className="flex items-center justify-between gap-2 border-b border-tcs-border px-4 py-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
          For finance
        </span>
        {stageLabel && (
          <span className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-secondary">
            {stageLabel}
          </span>
        )}
      </div>
      <dl className="divide-y divide-tcs-border px-4">
        <FinanceRow k="Facility">
          {facilityName ? (
            <>
              {facilityName}
              {facilityId && <span className="text-tcs-text-muted"> · {facilityId}</span>}
            </>
          ) : (
            <span className="inline-flex items-center gap-1 text-rag-red">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> none linked
            </span>
          )}
        </FinanceRow>
        {accountName && <FinanceRow k="Account">{accountName}</FinanceRow>}
        {classification && <FinanceRow k="Classified">{classification}</FinanceRow>}
        <FinanceRow k="Bill client">
          <span className="font-semibold tabular-nums">{fmtCurrency(bill)}</span>
        </FinanceRow>
        <FinanceRow k="Paid out to subs">
          <span className="tabular-nums">{fmtCurrency(paidOut)}</span>
        </FinanceRow>
        {chargedBack > 0 && (
          <FinanceRow k="Recovered from subs">
            <span className="tabular-nums text-st-danger-fg">−{fmtCurrency(chargedBack)}</span>
          </FinanceRow>
        )}
        <FinanceRow k={chargedBack > 0 ? 'Net sub cost' : 'Sub cost'}>
          <span className="font-semibold tabular-nums">{fmtCurrency(netCost)}</span>
        </FinanceRow>
        {/* Item #680 (TCSD-0380) — each service line pays its own sub; this one row
            replaces the separate Sub payment box, so the facts appear once. */}
        {linesPaid && (
          <FinanceRow k="Lines paid">
            <span className="tabular-nums">{linesPaid}</span>
          </FinanceRow>
        )}
        <FinanceRow k="Margin">
          {showMargin ? (
            <>
              <span className="tabular-nums">{fmtCurrency(marginAmount)}</span>{' '}
              <span className={`font-semibold ${marginColorClass(marginStatus)}`}>
                ({fmtPct(pct)})
              </span>
            </>
          ) : (
            <span className="text-tcs-text-muted">
              {marginStatus === 'Internal Cost'
                ? 'Internal cost'
                : classification
                  ? 'not billed — margin does not apply'
                  : '—'}
            </span>
          )}
        </FinanceRow>
        <FinanceRow k="Work done">
          {workDone ? fmtDate(workDone) : <span className="text-tcs-text-muted">not complete</span>}
        </FinanceRow>
      </dl>
    </div>
  );
}

export function FinanceRow({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2">
      <dt className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
        {k}
      </dt>
      <dd className="min-w-0 text-right text-[13px] text-tcs-text">{children}</dd>
    </div>
  );
}
